/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';

interface FollowState {
  isFollowing: boolean;
  followerCount: number;
}

interface FollowStateMap {
  [userId: number]: FollowState;
}

@Injectable({
  providedIn: 'root',
})
export class FollowStateService {
  private stateSubject = new BehaviorSubject<FollowStateMap>({});
  public state$: Observable<FollowStateMap> = this.stateSubject.asObservable();

  /**
   * Update the follow state for a specific user
   * @param userId - The ID of the user
   * @param isFollowing - Whether the current user is following this user
   * @param followerCount - The total number of followers for this user
   */
  updateFollowState(userId: number, isFollowing: boolean, followerCount: number): void {
    const currentState = this.stateSubject.value;
    const existingState = currentState[userId];

    // Only update if state has actually changed
    if (
      !existingState
      || existingState.isFollowing !== isFollowing
      || existingState.followerCount !== followerCount
    ) {
      this.stateSubject.next({
        ...currentState,
        [userId]: {
          isFollowing,
          followerCount,
        },
      });
    }
  }

  /**
   * Get an observable for a specific user's follow state
   * @param userId - The ID of the user
   * @returns Observable<FollowState | undefined> that emits when the user's follow state changes
   */
  getFollowState$(userId: number): Observable<FollowState | undefined> {
    return this.state$.pipe(
      map((state) => state[userId]),
      distinctUntilChanged((prev, curr) => {
        // Only emit if the state actually changed
        if (!prev && !curr) return true;
        if (!prev || !curr) return false;
        return prev.isFollowing === curr.isFollowing
          && prev.followerCount === curr.followerCount;
      }),
    );
  }
}
